<template>
  <BCard
    class="border rounded-lg"
    header-class="flex justify-between"
  >
    <template #header>
      <div class="text-black font-semibold text-xl">
        Ringkasan
      </div>
      <DateRangePicker
        ref="picker"
        v-model="dateRange"
        :locale-data="locale"
        opens="left"
        :show-ranges="false"
        :ranges="ranges"
      />
    </template>
    <BRow>
      <BCol
        cols="12"
        lg="8"
        md="12"
        sm="12"
      >
        <BOverlay
          :show="loading.summary"
          spinner-variant="primary"
          variant="light"
          class="mt-[16px]"
          blur="0"
          opacity=".5"
          rounded="sm"
        >
          <BCard class="border rounded-lg !bg-[#F8F8F8] mb-0">
            <template #header>
              <div class="flex items-center gap-3">
                <span class="border border-[#FCD4BE] bg-[#FCD4BE] px-[4px] py-[1px] rounded-lg k-card-coin text-primary text-lg" />
                <span class="text-lg font-medium">
                  Total Spending
                </span>
              </div>
            </template>
            <div class="text-black text-3xl font-semibold">
              {{ IDR(summary.total_spending) }}
            </div>
            <BCard
              class="mt-1 border border-[#E2E2E2] mb-0"
              header-class="!p-[8px]"
              body-class="!p-[8px]"
            >
              <template #header>
                <span>Total Decline Transaksi</span>
              </template>
              <div class="text-black text-xl font-medium">
                {{ summary.decline_transaction.total_decline }}x ({{ summary.decline_transaction.total_card_decline }} Kartu)
              </div>
              <BRow>
                <BCol
                  v-for="decline in summary.decline_transaction.list_declines"
                  :key="decline.decline_type"
                  cols="12"
                  lg="4"
                  md="4"
                  sm="12"
                >
                  <BCard
                    header-class="!p-[8px]"
                    body-class="!p-[8px]"
                    class="border rounded-lg !bg-[#F8F8F8] mt-[10px] mb-0 text-sm"
                  >
                    <template #header>
                      <span class="text-black font-medium">{{ decline.decline_type }}</span>
                    </template>
                    <div>Platinum : {{ decline.total_platinum_decline }}x ({{ decline.total_platinum_card_decline }} kartu)</div>
                    <div>Gold : {{ decline.total_gold_decline }}x ({{ decline.total_gold_card_decline }} kartu)</div>
                  </BCard>
                </BCol>
              </BRow>
            </BCard>
          </BCard>
        </BOverlay>
      </BCol>
      <BCol
        v-if="!isEmpty(categorySpending)"
        cols="12"
        lg="4"
        md="12"
        sm="12"
      >
        <BOverlay
          :show="loading.categorySpending"
          spinner-variant="primary"
          variant="light"
          class="mt-[16px]"
          blur="0"
          opacity=".5"
          rounded="sm"
        >

          <BCard class="border rounded-lg mb-0">
            <template #header>
              <span class="text-lg font-semibold">
                Kategori Pengeluaran
              </span>
            </template>
            <ApexChart
              ref="chart"
              type="donut"
              :options="options"
              :series="categorySpending"
            />
          </BCard>
        </BOverlay>
      </BCol>
    </BRow>
  </BCard>
</template>

<script>
import { LABELDAY, LABELMONTH, YEAR_MONTH_DAY } from '@/libs/filterDate'
import DateRangePicker from 'vue2-daterange-picker'
import {
  firstDateOfMonth, last30, last7, lastDateOfLastMonth, lastDateOfMonth, lastMonth, today, yesterday,
} from '@/store/helpers'
import { newAxiosIns } from '@/libs/axios'
import { IDR } from '@/libs/currency'
import isEmpty from 'lodash/isEmpty'
import ApexChart from 'vue-apexcharts'
import { spendingCategoryOptions } from './config'

export default {
  components: { DateRangePicker, ApexChart },
  data() {
    return {
      IDR,
      isEmpty,
      categorySpending: [],
      options: spendingCategoryOptions,
      summary: null,
      loading: {
        summary: false,
        categorySpending: false,
      },
      dateRange: {
        startDate: firstDateOfMonth,
        endDate: today,
      },
      locale: {
        format: 'dd mmm yyyy',
        daysOfWeek: LABELDAY,
        monthNames: LABELMONTH,
        applyLabel: 'Pilih',
        cancelLabel: 'Kembali',
      },
      ranges: {
        'Hari ini': [today, today],
        Kemarin: [yesterday, yesterday],
        '7 Hari Terakhir': [last7, today],
        '30 Hari Terakhir': [last30, today],
        'Bulan ini': [firstDateOfMonth, lastDateOfMonth],
        'Bulan Lalu': [lastMonth(1), lastDateOfLastMonth],
      },
    }
  },
  watch: {
    dateRange: {
      handler() {
        this.getSummary()
        this.getCategorySpending()
      },
    },
  },
  mounted() {
    this.getSummary()
    this.getCategorySpending()
  },
  methods: {
    async getSummary() {
      this.loading.summary = true
      try {
        const { id } = this.$route.params
        const params = {
          start_date: YEAR_MONTH_DAY(this.dateRange.startDate),
          end_date: YEAR_MONTH_DAY(this.dateRange.endDate),
        }
        const url = `/komcards/api/v1/user/${id}/summary`
        const { data: { data } } = await newAxiosIns.get(url, { params })
        this.summary = data
        this.loading.summary = false
      } catch {
        this.$toast_error({ message: 'Gagal load data ringkasan' })
        this.loading.summary = false
      }
    },
    async getCategorySpending() {
      this.loading.categorySpending = true
      try {
        const { id } = this.$route.params
        const params = {
          start_date: YEAR_MONTH_DAY(this.dateRange.startDate),
          end_date: YEAR_MONTH_DAY(this.dateRange.endDate),
        }
        const url = `/komcards/api/v1/user/${id}/category-spending`
        const { data: { data } } = await newAxiosIns.get(url, { params })

        this.categorySpending = data.spending_category.map(item => Math.round(item.percentage))
        this.options = {
          ...this.options,
          labels: data.spending_category.map(item => `<span class='capitalize'>${item.category} ${Math.round(item.percentage)}%</span>`),
          colors: ['#FFD93D', '#FA812F', '#73C8D2', '#6F00FF', '#73C8D2'],
          xaxis: {
            ...this.options.xaxis,
            categories: data.spending_category.map(item => Math.round(item.percentage)),
          },
        }
        this.loading.categorySpending = false
      } catch {
        // this.$toast_error({ message: 'Gagal load data category spending' })
        this.loading.categorySpending = false
      }
    },
  },
}
</script>
